import { rem } from 'polished'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { RouteComponentProps } from 'react-router'
import styled from 'styled-components'

import TripSummary from './Summary'

import Breakpoint from 'components/Common/Breakpoint'
import HeadData from 'components/Common/HeadData/HeadData'
import FloatingActionButton from 'components/Luxkit/Button/FloatingActionButton'
import LineHeartIcon from 'components/Luxkit/Icons/line/LineHeartIcon'
import LineMapIcon from 'components/Luxkit/Icons/line/LineMapIcon'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import ModalProvider from 'contexts/ModalProvider'
import usePendingLoginHandler from 'hooks/usePendingLoginHandler'
import zIndex from 'styles/tools/z-index'
import SaveButtonOnboardingTooltip from 'tripPlanner/components/CuratedTrip/SaveButtonOnboardingTooltip'
import CuratedTripHeader from 'tripPlanner/components/CuratedTripHeader'
import CuratedHeaderFull from 'tripPlanner/components/Header/CuratedHeaderFull'
import HeaderBasic from 'tripPlanner/components/Header/HeaderBasic'
import Redirect from 'tripPlanner/components/Redirect'
import { DesktopTripMap, MobileTripMap } from 'tripPlanner/components/TripMaps'
import TripModal from 'tripPlanner/components/TripModal'
import CopyTemplate from 'tripPlanner/components/TripModal/Templates/CopyTemplate'
import { headerHeight, tripItemsMaxWidth } from 'tripPlanner/config'
import HeaderInViewContextProvider from 'tripPlanner/contexts/HeaderInViewContext'
import TripContext, { TripContextValue } from 'tripPlanner/contexts/TripContext'
import { useCuratedTripMetadata, useCuratedTrip } from 'tripPlanner/hooks/api/curatedTrip'
import useSaveButtonOnboardingTooltipRules from 'tripPlanner/hooks/onboarding/useSaveButtonOnboardingTooltipRules'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useIsScrolledToTop from 'tripPlanner/hooks/useIsScrolledToTop'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import LoadingPage from 'tripPlanner/pages/Loading'
import { getTripDescriptors } from 'tripPlanner/utils/curated/curatedTrips'

const RightFloatingContainer = styled.div`
  position: fixed;
  z-index: ${zIndex.floatingPanel};
  width: min-content;
  right: ${rem(20)};
  bottom: ${rem(20)};
`

const LeftFloatingContainer = styled.div`
  position: fixed;
  z-index: ${zIndex.floatingPanel};
  left: ${rem(20)};
  bottom: ${rem(20)};
`

// Making space for the Genesys chat icon
const Content = styled.div`
  ${mediaQueryUp.largeDesktop} {
    padding-bottom: ${rem(64)};
  }
`

const MapContainer = styled.div`
  position: sticky;
  z-index: 0;
  top: ${headerHeight.sm};
  height: calc(100vh - ${headerHeight.sm});

  ${mediaQueryUp.desktop} {
    top: ${headerHeight.lg};
    height: calc(100vh - ${headerHeight.lg});
  }

  ${mediaQueryDown.tablet} {
    display: none;
  }

  ${LeftFloatingContainer} {
    position: absolute;
  }
`

const Root = styled.div`
  display: grid;
  grid-template-columns: clamp(300px, 55%, ${tripItemsMaxWidth}) 1fr;
  background: ${(props) => props.theme.palette.neutral.default.eight};
  min-height: calc(var(--vh) * 100);

  ${mediaQueryDown.tablet} {
    grid-template-columns: 100%;
  }
`

type MatchParams = { tripId: string; tab: string }

interface Props extends RouteComponentProps<MatchParams> {}

function CuratedTripParent(props: Props) {
  const { match } = props
  const tripId = match.params.tripId

  const { isTooltipOpen, setTooltipWasDismissed } =
    useSaveButtonOnboardingTooltipRules()
  const saveButtonRef = useRef<HTMLButtonElement | null>(null)

  const {
    isLoading,
    isError,
    data: curatedTrip,
    error: tripError,
  } = useCuratedTrip({ tripId })

  const { data: metadata } = useCuratedTripMetadata({ tripId })

  const descriptors = useMemo(() => getTripDescriptors(metadata), [metadata])

  const openModal = useOpenModal()
  const { removeImmersiveMode } = useImmersiveModeHandlers()

  const onSave = useCallback(() => {
    if (curatedTrip) {
      openModal(CopyTemplate, {
        template: curatedTrip,
      })
    }
  }, [openModal, curatedTrip])

  const onSaveClick = usePendingLoginHandler(onSave, 'tripPlannerLogin')

  const { name: tripName = '' } = curatedTrip ?? {}

  // currentTripId is used to show the immersive header. It should only be
  // set on actions we want to be immersive. Since it persists on return
  // we make sure to clear it.
  useEffect(() => {
    removeImmersiveMode()
  }, [removeImmersiveMode])

  const isScrolledToTop = useIsScrolledToTop({ threshold: 1 })

  const tripContextValue = useMemo<TripContextValue>(
    () => ({
      tripId,
      viewAsTemplate: true,
      isPublicTrip: false,
      permissions: {
        canEdit: false,
        canAdmin: false,
        canPublish: false,
      },
      name: curatedTrip?.name ?? '',
      displayContext: 'tripPlanner',
    }),
    [curatedTrip?.name, tripId],
  )

  if (isLoading) return <LoadingPage />
  if (isError && tripError.status === 404)
  { return (
    <>
      <HeaderBasic backTo="/trip-planner/curated" backLabel="Curated trips" />
      <Error404PageWithOffers />
    </>
  ) }
  if (isError) return <Redirect to="/trip-planner" />

  return (
    <HeaderInViewContextProvider>
      <TripContext.Provider value={tripContextValue}>
        <ModalProvider>
          <ModalProvider>
            <HeadData
              title={`${curatedTrip.name} - Curated Trip`}
              description={descriptors.join(' • ')}
              imageId={curatedTrip?.imageId}
            />
            <TripModal />
            <CuratedHeaderFull trip={curatedTrip} />
            <Root>
              <Breakpoint max="tablet">
                <LeftFloatingContainer>
                  <FloatingActionButton
                    isCollapsed={!isScrolledToTop}
                    variant="ghost"
                    startIcon={<LineMapIcon />}
                    onClick={() =>
                      openModal(MobileTripMap, {
                        title: tripName,
                        tab: 'Summary',
                        trip: curatedTrip,
                        canEdit: false,
                        useCardCarousel: true,
                        isTripLoading: isLoading,
                      })
                    }
                  >
                    Map
                  </FloatingActionButton>
                </LeftFloatingContainer>
                <RightFloatingContainer>
                  <FloatingActionButton
                    onClick={onSaveClick}
                    isCollapsed={!isScrolledToTop}
                    variant="ghost"
                    startIcon={<LineHeartIcon />}
                    ref={saveButtonRef}
                  >
                    Save trip
                  </FloatingActionButton>
                  <SaveButtonOnboardingTooltip
                    anchorRef={saveButtonRef}
                    isOpen={isTooltipOpen}
                    onDismiss={setTooltipWasDismissed}
                    placement="top-end"
                  />
                </RightFloatingContainer>
              </Breakpoint>
              <Content>
                <CuratedTripHeader
                  trip={curatedTrip}
                  descriptors={descriptors}
                />
                <TripSummary trip={curatedTrip} />
              </Content>
              <MapContainer>
                <DesktopTripMap
                  title={tripName}
                  tab="Summary"
                  trip={curatedTrip}
                  canEdit={false}
                  useCardCarousel={false}
                  isTripLoading={isLoading}
                />
              </MapContainer>
            </Root>
          </ModalProvider>
        </ModalProvider>
      </TripContext.Provider>
    </HeaderInViewContextProvider>
  )
}

export default CuratedTripParent
